import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import axios from '../common/axios'
import { IRequest } from '../common/IRequest'
import { IStateRedux } from '../common/ReduxStore'
import { toast } from 'react-toastify'
import { UsuarioModel } from '../models/UsuarioModel'
import { broadcastLogin, broadcastLogout, broadcastCursoChange, onAuthChange } from './authChannel'

export interface IState {
  usuario: UsuarioModel | null
  matriculaId: number | null
  cursoId: number | null
  isAuthenticated: boolean
  senhaMudada: boolean
  shouldChangePassword: boolean
  request: IRequest
}

export const initialState: IState = {
  usuario: null,
  matriculaId: null,
  cursoId: null,
  isAuthenticated: false,
  senhaMudada: false,
  shouldChangePassword: false,
  request: {
    fetching: false,
    errorCode: null,
    message: '',
  },
}

const { reducer: authReducer, actions } = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    loginFetch(state) {
      state.request.fetching = true
    },
    loginSuccess(state, { payload }: PayloadAction<{ usuario: UsuarioModel; matriculaId: number | null; cursoId: number | null }>) {
      state.request = { ...initialState.request, fetching: false }
      state.usuario = payload.usuario
      state.matriculaId = payload.matriculaId
      state.cursoId = payload.cursoId
      state.isAuthenticated = true
    },
    loginError(state, { payload }: PayloadAction<{ errorCode; message }>) {
      state.request = {
        errorCode: payload.errorCode,
        message: payload.message,
        fetching: false,
      }
    },
    logout(state) {
      state.usuario = null
      state.matriculaId = null
      state.isAuthenticated = false
    },
    setSession(state, { payload }: PayloadAction<{ usuario: UsuarioModel | null; matriculaId: number | null; cursoId: number | null; shouldChangePassword?: boolean }>) {
      state.usuario = payload.usuario
      state.matriculaId = payload.matriculaId
      state.cursoId = payload.cursoId
      state.isAuthenticated = !!payload.usuario
      state.shouldChangePassword = !!payload.shouldChangePassword
    },
    passwordDefined(state) {
      state.shouldChangePassword = false
    },
    recuperarSenha(state): IState {
      state.request.fetching = true
      return state
    },
    recuperarSenhaSuccess(state) {
      state.request = { ...initialState.request, fetching: false }
    },
    recuperarSenhaError(state, { payload }: PayloadAction<{ errorCode; message }>) {
      state.request = {
        errorCode: payload.errorCode,
        message: payload.message,
        fetching: false,
      }
    },
    mudarSenha(state): IState {
      state.request.fetching = true
      return state
    },
    mudarSenhaSuccess(state) {
      state.request = { ...initialState.request, fetching: false }
      state.senhaMudada = true
    },
    mudarSenhaError(state, { payload }: any) {
      state.request = {
        errorCode: payload.errorCode,
        message: payload.message,
        fetching: false,
      }
    },
  },
})

const authActions = {
  ...actions,
  login({ cpf, senha }: { cpf: string; senha: string }) {
    return async (dispatch: any) => {
      dispatch(actions.loginFetch())
      try {
        const data = await axios.Usuarios.login(cpf, senha)
        dispatch(actions.loginSuccess({ usuario: data.usuario, matriculaId: data.matriculaId, cursoId: data.cursoId ?? null }))
        broadcastLogin(data.usuario.id)
      } catch (error: any) {
        const message = error.response?.data?.message || 'Falha ao realizar login'
        dispatch(actions.loginError({
          errorCode: error.response?.status ?? 400,
          message,
        }))
        toast(message)
      }
    }
  },
  checkSession() {
    return async (dispatch: any) => {
      try {
        const data = await axios.Usuarios.getSession()
        dispatch(actions.setSession({ usuario: data.usuario, matriculaId: data.matriculaId, cursoId: data.cursoId, shouldChangePassword: data.shouldChangePassword }))
      } catch {
        dispatch(actions.logout())
      }
    }
  },
  newTokenMatricula({ matriculaId, callback }: { matriculaId: string; callback: () => void }) {
    return async (dispatch: any) => {
      dispatch(actions.loginFetch())
      try {
        const data = await axios.Usuarios.newTokenMatricula(matriculaId)
        dispatch(actions.loginSuccess({ usuario: data.usuario, matriculaId: data.matriculaId, cursoId: data.cursoId }))
        console.log('[AuthStore] newTokenMatricula data.cursoId:', data.cursoId)
        if (data.cursoId) broadcastCursoChange(data.cursoId)
        setTimeout(() => {
          callback?.()
        }, 1000)
      } catch (error: any) {
        dispatch(actions.loginError({ errorCode: 400, message: 'Falha ao realizar login' }))
        toast(error.response?.data?.message || 'Falha ao realizar login')
      }
    }
  },
  doLogout() {
    return async (dispatch: any) => {
      try {
        await axios.Usuarios.logout()
      } catch {
        // ignore logout errors
      }
      dispatch(actions.logout())
      broadcastLogout()
    }
  },
  recuperaSenha({
    cpf,
    callback,
    errorCallback,
  }: {
    cpf: string
    callback?: (dados: { primeiroNome: string; emailMascarado: string }) => void
    errorCallback?: (message: string) => void
  }) {
    return async (dispatch: any) => {
      dispatch(actions.recuperarSenha())
      try {
        const dados = await axios.Usuarios.recuperarSenha(cpf)
        dispatch(actions.recuperarSenhaSuccess())
        callback?.(dados)
        toast('E-mail de recuperação de senha enviado')
      } catch (error: any) {
        const message =
          error.response?.data?.message ??
          error.message ??
          'Falha ao enviar o e-mail de recuperação de senha'
        dispatch(actions.recuperarSenhaError({
          errorCode: error.response?.status ?? 400,
          message,
        }))
        errorCallback?.(message)
        toast(message)
      }
    }
  },
  mudarSenhaToken({ senha, token }: { senha: string; token: string }) {
    return async (dispatch: any) => {
      dispatch(actions.mudarSenha())
      try {
        await axios.Usuarios.alterarSenhaToken(senha, token)
        dispatch(actions.mudarSenhaSuccess())
        toast('Senha alterada com sucesso')
      } catch (error: any) {
        dispatch(actions.mudarSenhaError(error))
        toast('Erro ao alterar senha')
      }
    }
  },
  alterarSenha({
    usuarioId,
    senhaAtual,
    novaSenha,
  }: {
    usuarioId: string
    senhaAtual: string
    novaSenha: string
  }) {
    return async (dispatch: any) => {
      dispatch(actions.mudarSenha())
      try {
        await axios.Usuarios.alterarSenha(usuarioId, senhaAtual, novaSenha)
        dispatch(actions.mudarSenhaSuccess())
        toast('Senha alterada com sucesso')
      } catch (error: any) {
        dispatch(actions.mudarSenhaError(error))
        toast(error.response?.data?.message ?? 'Erro ao alterar senha')
      }
    }
  },
}

export const getUsuarioLogado = createSelector(
  (s: IStateRedux) => s.auth.usuario,
  (usuario) => usuario ?? ({} as UsuarioModel)
)

export function useAuthSync() {
  const dispatch = useDispatch()
  const usuarioId = useSelector((s: IStateRedux) => s.auth.usuario?.id)

  useEffect(() => {
    return onAuthChange((msg) => {
      if (msg.type === 'LOGIN' && msg.usuarioId !== usuarioId) {
        window.location.reload()
      }
      if (msg.type === 'LOGOUT') {
        dispatch(actions.logout())
        window.location.href = '/'
      }
    })
  }, [usuarioId, dispatch])
}

export { authReducer, authActions }
